import React, { useState } from 'react';
import { DateTime } from 'luxon';
import { Button, Calendar, Fieldset, Input, Tooltip } from '@flixbus/honeycomb-react';
import { Icon, IconCalendar } from '@flixbus/honeycomb-icons-react';

import { useVoucherFormContext } from '../../../Context';
import { Asterisk } from '../../../Asterisk';
import { getNextYearDateOnly, toISODateEndOfDay } from '../../../../../helpers/dateTime';

const VoucherFormValidUntil: React.FC<{ disabled: boolean; busPartner: boolean }> = ({
  disabled,
  busPartner,
}) => {
  const {
    formState: {
      payload: { validSince, validUntil },
    },
    handlePayloadChange,
  } = useVoucherFormContext();

  const [calendarOpen, setCalendarOpen] = useState(false);

  const dateObject = validUntil ? DateTime.fromISO(validUntil) : null;
  const sinceDate = DateTime.fromISO(validSince);
  const minDate = busPartner
    ? sinceDate.plus({ days: 1 }).toFormat('yyyy-MM-dd')
    : sinceDate.toFormat('yyyy-MM-dd');

  const maxDate = busPartner ? getNextYearDateOnly() : undefined;

  const disableDate = (date: Date) => {
    const min = new Date(minDate);
    min.setHours(0, 0, 0, 0);
    if (date < min) return true;
    if (maxDate) {
      const max = new Date(maxDate);
      max.setHours(23, 59, 59, 999);
      if (date > max) return true;
    }
    return false;
  };

  const calendarEndDate = maxDate
    ? new Date(maxDate)
    : new Date(new Date().setFullYear(new Date().getFullYear() + 5));

  return (
    <Fieldset itemSpacing='half'>
      <Tooltip
        id='calendar-validUntil-tooltip'
        active={calendarOpen && !disabled}
        disableInternalHandlers
        onToggle={() => setCalendarOpen(false)}
        position='bottom'
        alignment='start'
        size='content-fit'
        wrapAround='block'
        extraClasses='calendar-tooltip'
        content={
          <Calendar
            id='calendar-validUntil'
            appearance='compact'
            startDate={new Date(minDate)}
            endDate={calendarEndDate}
            selected={dateObject?.isValid ? dateObject.toJSDate() : undefined}
            showCurrentDate
            handleSelect={date => {
              const value = toISODateEndOfDay(DateTime.fromJSDate(date));
              handlePayloadChange({ value, prop: 'validUntil' });
              setCalendarOpen(false);
            }}
            disableDayCondition={disableDate}
          />
        }
      >
        <Input
          name='date-field-validUntil'
          id='date-field-validUntil'
          label={<>Voucher valid until {!disabled && <Asterisk />}</>}
          type='text'
          readOnly
          disabled={disabled}
          required
          info={`The validity ends at 23:59 GMT${DateTime.local().toFormat('ZZ')}`}
          value={dateObject?.isValid ? dateObject.toFormat('dd.MM.yyyy') : ''}
          placeholder='DD.MM.YYYY'
          onClick={() => !disabled && setCalendarOpen(prev => !prev)}
          iconLeft={
            <Button
              aria-label='Open calendar'
              disabled={disabled}
              onClick={() => setCalendarOpen(prev => !prev)}
            >
              <Icon appearance='primary' InlineIcon={IconCalendar} />
            </Button>
          }
        />
      </Tooltip>
    </Fieldset>
  );
};

export default VoucherFormValidUntil;
